import { useEffect, useRef, useState } from "react";
import { fmt } from "@/lib/studioUtils";

// Thin timeline under the video: playhead progress plus a marker per comment.
// Drag a marker to retime its comment; click it to jump there and edit it.
export function CommentTimeline({ playerRef, comments, onSeek, onRetime, onEdit, speakingId, disabled }) {
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hover, setHover] = useState(null);
  const [drag, setDrag] = useState(null); // { id, t }
  const trackRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const iv = setInterval(() => {
      const p = playerRef.current;
      try {
        if (!p?.getDuration) return;
        setDuration(p.getDuration() || 0);
        setTime(p.getCurrentTime() || 0);
      } catch (e) { /* player not ready */ }
    }, 250);
    return () => clearInterval(iv);
  }, [playerRef]);

  const pct = (t) => `${Math.min(100, Math.max(0, duration ? (t / duration) * 100 : 0))}%`;
  const timeAt = (clientX) => {
    const r = trackRef.current.getBoundingClientRect();
    return Math.min(duration, Math.max(0, ((clientX - r.left) / r.width) * duration));
  };
  const seekFromBar = (e) => { if (!disabled && duration) onSeek(timeAt(e.clientX)); };

  const down = (c) => (e) => {
    if (disabled) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { c, startX: e.clientX, moved: false };
  };
  const move = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (!d.moved && Math.abs(e.clientX - d.startX) < 4) return;
    d.moved = true;
    setDrag({ id: d.c.id, t: Math.round(timeAt(e.clientX) * 10) / 10 });
  };
  const up = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    if (d.moved && drag) { onRetime(d.c.id, drag.t); onSeek(drag.t); } else onEdit(d.c);
    setDrag(null);
  };

  const shown = drag ? { ...comments.find((c) => c.id === drag.id), t: drag.t } : hover;

  return (
    <div className="mt-3 select-none" data-testid="comment-timeline">
      <div className="relative h-6 flex items-center">
        <div ref={trackRef} onClick={seekFromBar} data-testid="timeline-track"
          className={`absolute inset-x-0 h-1.5 rounded-full bg-zinc-200 ${disabled ? "" : "cursor-pointer"}`}>
          <div className="absolute inset-y-0 left-0 rounded-full bg-zinc-400" style={{ width: pct(time) }} data-testid="timeline-progress" />
        </div>
        {duration > 0 && comments.map((c) => {
          const t = drag?.id === c.id ? drag.t : c.t;
          return (
            <button key={c.id} type="button" disabled={disabled} data-testid={`timeline-marker-${c.id}`} title="Click to edit · drag to move"
              onPointerDown={down(c)} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
              onMouseEnter={() => setHover(c)} onMouseLeave={() => setHover(null)}
              className={`absolute -translate-x-1/2 w-4 h-4 rounded-full border-2 border-white shadow touch-none transition-transform hover:scale-125 ${drag?.id === c.id ? "scale-150 bg-red-700 cursor-grabbing" : speakingId === c.id ? "bg-yellow-400 scale-125" : "bg-red-600 cursor-grab"}`}
              style={{ left: pct(t) }} />
          );
        })}
        {shown && (
          <div className="absolute bottom-full mb-1 -translate-x-1/2 max-w-[240px] bg-zinc-900 text-white text-xs rounded-[4px] px-2 py-1 pointer-events-none z-10"
            style={{ left: pct(shown.t) }} data-testid="timeline-tooltip">
            <span className="font-mono text-yellow-400 mr-1.5">{fmt(Math.floor(shown.t))}</span>
            <span className="line-clamp-2">{shown.text}</span>
          </div>
        )}
      </div>
      <div className="flex justify-between text-[11px] font-mono text-zinc-400" data-testid="timeline-times">
        <span>{fmt(Math.floor(time))}</span><span>{fmt(Math.floor(duration))}</span>
      </div>
    </div>
  );
}
